import { useState, useEffect } from "react";
import { ExternalLink, ChevronDown, ChevronUp, Lock, Settings, X, ArrowRight } from "lucide-react";
import ScoreRing from "./ScoreRing";
import {
  DOMAIN_META,
  DOMAIN_ORDER,
  groupRatingsByDomain,
  computeDomainScore,
  computeCompositeScore,
  computeConfidenceDots,
  getLastUpdated,
} from "./scoring";


function StatusBadge({ status }) {
  const map = {
    Certified: "badge-certified",
    Assessed: "badge-certified",
    "Not Certified": "badge-not-certified",
    Unknown: "badge-unknown",
    Expired: "badge-expired",
  };
  return (
    <span className={map[status] || "badge-unknown"} data-testid="status-badge">
      {status || "Unknown"}
    </span>
  );
}

function DomainRow({ domainName, ratings, isExpanded, onToggle, isPremium, comingSoon, weights, brandSize, brandCategory }) {
  const score = computeDomainScore(ratings || [], brandSize || "", brandCategory || "");
  const meta = DOMAIN_META[domainName];

  if (!meta) return null;

  return (
    <div
      className="domain-row"
      data-testid={`domain-row-${meta.key}`}
      style={{ borderBottom: "1px solid var(--border)" }}
    >
      <button
        className="domain-row-header"
        onClick={isPremium && !comingSoon ? () => window.open("https://coral.sh/pro", "_blank") : onToggle}
        data-testid={`domain-toggle-${meta.key}`}
        style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "10px 16px",
          background: "none",
          border: "none",
          cursor: isPremium ? (comingSoon ? "default" : "pointer") : "pointer",
          textAlign: "left",
          transition: "background 0.15s",
        }}
        onMouseEnter={isPremium && !comingSoon ? (e) => { e.currentTarget.style.background = "#FFF8F7"; } : undefined}
        onMouseLeave={isPremium && !comingSoon ? (e) => { e.currentTarget.style.background = "none"; } : undefined}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "8px", flex: 1, opacity: isPremium ? 0.6 : 1 }}>
          <span style={{ fontSize: "16px" }}>{meta.emoji}</span>
          <span style={{ fontSize: "13px", fontWeight: 500, color: "var(--text-primary)" }}>
            {meta.label}
          </span>
        </div>

        {isPremium ? (
          <span style={{ fontSize: "11px", fontWeight: 600, color: "#FA7268", whiteSpace: "nowrap" }}>
            {comingSoon ? "Coming soon" : "Unlock with Coral Pro →"}
          </span>
        ) : (
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            {score != null ? (
              <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                <div style={{
                  width: "48px",
                  height: "4px",
                  background: "#F3F3F3",
                  borderRadius: "2px",
                  overflow: "hidden",
                }}>
                  <div style={{
                    width: `${(score / 10) * 100}%`,
                    height: "100%",
                    background: "#FA7268",
                    borderRadius: "2px",
                    transition: "width 0.6s ease-out",
                  }} />
                </div>
                <span style={{ fontSize: "12px", fontWeight: 600, color: "var(--text-primary)", minWidth: "24px" }}>
                  {score.toFixed(1)}
                </span>
              </div>
            ) : (
              <span style={{ color: "#AAAAAA", fontStyle: "italic", fontSize: "12px" }}>No data yet</span>
            )}
            {isExpanded
              ? <ChevronUp size={14} color="#999" />
              : <ChevronDown size={14} color="#999" />
            }
          </div>
        )}
      </button>

      {!isPremium && isExpanded && (
        <div
          className="fade-in"
          style={{ padding: "0 16px 10px" }}
          data-testid={`domain-details-${meta.key}`}
        >
          {(ratings || []).length === 0 ? (
            <p style={{ fontSize: "12px", color: "var(--text-tertiary)", padding: "4px 0" }}>
              No certifications in this domain
            </p>
          ) : (
            ratings.map((r) => (
              <div
                key={r.id}
                style={{
                  padding: "8px 0",
                  borderTop: "1px solid #F5F5F5",
                  display: "flex",
                  alignItems: "flex-start",
                  justifyContent: "space-between",
                  gap: "8px",
                }}
                data-testid="certifier-row"
              >
                <div style={{ flex: 1 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                    <span style={{ fontSize: "12px", fontWeight: 500, color: "var(--text-primary)" }}>
                      {r.certifier?.name || "Unknown Certifier"}
                    </span>
                    {r.certifier?.source_url && (
                      <a
                        href={r.certifier.source_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="certifier-link"
                        style={{ color: "var(--text-tertiary)", lineHeight: 0 }}
                      >
                        <ExternalLink size={11} />
                      </a>
                    )}
                  </div>
                  <div style={{ fontSize: "11px", color: "var(--text-tertiary)", marginTop: "2px" }}>
                    {r.data_vintage && `Verified ${r.data_vintage}`}
                    {r.certification_scope && r.certification_scope !== "Whole Brand" && (
                      <span style={{ marginLeft: "6px", color: "#FD7E14" }}>
                        ⚠ {r.certification_scope}
                      </span>
                    )}
                  </div>
                </div>
                <StatusBadge status={r.status} />
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

export default function ExtensionPopup({
  brandData,
  loading,
  preferences,
  onOpenSettings,
  onClose,
  alternatives = [],
}) {
  const [expandedDomain, setExpandedDomain] = useState(null);

  const { weights = {}, toggles = {} } = preferences || {};

  // Compute scores
  let compositeScore = null;
  let groupedRatings = {};
  let domainScores = {};
  let dots = [];

  if (brandData?.found && brandData.ratings) {
    const brandSize     = brandData.brand?.brand_size || "";
    const brandCategory = brandData.brand?.category || "";
    groupedRatings = groupRatingsByDomain(brandData.ratings);
    for (const [name, ratings] of Object.entries(groupedRatings)) {
      domainScores[name] = computeDomainScore(ratings, brandSize, brandCategory);
    }
    compositeScore = computeCompositeScore(domainScores, weights, toggles);
    dots = computeConfidenceDots(brandData.ratings, toggles, brandCategory);
  }

  // Emoji indicators: unchanged — any relevant Certified/Assessed/Not Certified = filled
  const filledDots = dots.filter(d => d.hasDot).length;
  // Ring threshold: count active+toggled domains that actually contribute a number
  const activeDomainsWithScore = DOMAIN_ORDER.filter(name => {
    const meta = DOMAIN_META[name];
    return meta && toggles[meta.key] && domainScores[name] != null;
  }).length;
  const showRing = activeDomainsWithScore >= 2;

  const lastUpdated = brandData?.ratings ? getLastUpdated(brandData.ratings) : "";

  // Compute alternative scores (use each alt's own brand_size and category)
  const altScores = alternatives.slice(0, 2).map((alt) => {
    const altBrandSize     = alt.brand?.brand_size || "";
    const altBrandCategory = alt.brand?.category || "";
    const gr = groupRatingsByDomain(alt.ratings || []);
    const ds = {};
    for (const [n, r] of Object.entries(gr)) ds[n] = computeDomainScore(r, altBrandSize, altBrandCategory);
    const composite = computeCompositeScore(ds, weights, toggles);
    return { ...alt.brand, composite };
  }).filter((a) => a.composite != null).sort((a, b) => b.composite - a.composite).slice(0, 2);

  if (loading) {
    return (
      <div data-testid="popup-loading" style={popupStyle}>
        <div style={headerStyle}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <CoralLogo />
            <span style={{ fontSize: "13px", color: "var(--text-secondary)" }}>
              Looking up brand...
            </span>
          </div>
          {onClose && <CloseBtn onClose={onClose} />}
        </div>
        <div style={{ padding: "40px 16px", textAlign: "center" }}>
          <div className="loading-spinner" />
          <p style={{ fontSize: "13px", color: "var(--text-secondary)", marginTop: "12px" }}>
            Fetching ethical ratings...
          </p>
        </div>
      </div>
    );
  }

  if (!brandData || !brandData.found) {
    return (
      <div data-testid="popup-demo" style={popupStyle}>
        {/* Header */}
        <div style={headerStyle}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <div style={{
              width: "28px", height: "28px", background: "#1A1A1A",
              borderRadius: "8px", display: "flex", alignItems: "center",
              justifyContent: "center", flexShrink: 0,
            }}>
              <span style={{ color: "#fff", fontSize: "13px", fontWeight: 700, fontFamily: "Manrope" }}>P</span>
            </div>
            <div>
              <div style={{ fontWeight: 700, fontSize: "14px", fontFamily: "Manrope", color: "var(--text-primary)" }}>
                Patagonia
              </div>
              <div style={{ fontSize: "11px", color: "var(--text-secondary)" }}>Apparel</div>
            </div>
          </div>
          {onClose && <CloseBtn onClose={onClose} />}
        </div>

        {/* Score ring */}
        <div style={{
          display: "flex", flexDirection: "column", alignItems: "center",
          padding: "16px", borderBottom: "1px solid var(--border)", flexShrink: 0,
        }}>
          <ScoreRing score={8.4} size={120} />
          <p style={{ fontSize: "12px", color: "var(--text-secondary)", marginTop: "6px" }}>
            Based on your values
          </p>
        </div>

        {/* Domain rows */}
        <div className="popup-scroll" style={{ flex: 1, overflowY: "auto" }}>
          {[
            { emoji: "🌿", label: "Environmental", score: 9.0 },
            { emoji: "👷", label: "Labor & Fair Trade", score: 7.8 },
            { emoji: "🐾", label: "Animal Welfare", score: null },
          ].map(({ emoji, label, score }) => (
            <div key={label} style={{ borderBottom: "1px solid var(--border)" }}>
              <div style={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                padding: "10px 16px",
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <span style={{
                    fontSize: "16px",
                    filter: score == null ? "grayscale(100%)" : "none",
                    opacity: score == null ? 0.35 : 1,
                  }}>{emoji}</span>
                  <span style={{ fontSize: "13px", fontWeight: 500, color: "var(--text-primary)" }}>{label}</span>
                </div>
                {score != null ? (
                  <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                    <div style={{ width: "48px", height: "4px", background: "#F3F3F3", borderRadius: "2px", overflow: "hidden" }}>
                      <div style={{ width: `${(score / 10) * 100}%`, height: "100%", background: "#FA7268", borderRadius: "2px" }} />
                    </div>
                    <span style={{ fontSize: "12px", fontWeight: 600, color: "var(--text-primary)", minWidth: "24px" }}>
                      {score.toFixed(1)}
                    </span>
                    <ChevronDown size={14} color="#999" />
                  </div>
                ) : (
                  <span style={{ color: "#AAAAAA", fontStyle: "italic", fontSize: "12px" }}>No data yet</span>
                )}
              </div>
            </div>
          ))}
          {[
            { emoji: "🏳️‍🌈", label: "LGBTQ+ Equality" },
            { emoji: "🏪", label: "Business Type" },
          ].map(({ emoji, label }) => (
            <div key={label} style={{ borderBottom: "1px solid var(--border)" }}>
              <div
                style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 16px", cursor: "pointer", transition: "background 0.15s" }}
                onClick={() => window.open("https://coral.sh/pro", "_blank")}
                onMouseEnter={(e) => e.currentTarget.style.background = "#FFF8F7"}
                onMouseLeave={(e) => e.currentTarget.style.background = "none"}
              >
                <div style={{ display: "flex", alignItems: "center", gap: "8px", opacity: 0.55 }}>
                  <span style={{ fontSize: "16px" }}>{emoji}</span>
                  <span style={{ fontSize: "13px", fontWeight: 500, color: "var(--text-primary)" }}>{label}</span>
                </div>
                <span style={{ fontSize: "11px", fontWeight: 600, color: "#FA7268", whiteSpace: "nowrap" }}>
                  Unlock with Coral Pro →
                </span>
              </div>
            </div>
          ))}
          <div style={{ borderBottom: "1px solid var(--border)" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 16px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "8px", opacity: 0.2 }}>
                <span style={{ fontSize: "16px" }}>🌍</span>
                <span style={{ fontSize: "13px", fontWeight: 500, color: "var(--text-primary)" }}>Country of Origin</span>
              </div>
              <span style={{ fontSize: "11px", color: "#aaa", fontStyle: "italic" }}>Coming soon</span>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div style={footerStyle} data-testid="popup-footer">
          <span style={{ fontSize: "11px", color: "var(--text-tertiary)" }}>Updated 2024</span>
          {onOpenSettings && (
            <button onClick={onOpenSettings} style={footerLinkStyle} data-testid="settings-link">
              <Settings size={12} /> My values
            </button>
          )}
        </div>
      </div>
    );
  }

  const { brand } = brandData;

  return (
    <div data-testid="popup-main" style={popupStyle}>
      {/* Header */}
      <div style={headerStyle}>
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <CoralLogo />
          <div>
            <div style={{ fontWeight: 700, fontSize: "14px", fontFamily: "Manrope", color: "var(--text-primary)" }}
              data-testid="brand-name">
              {brand.name}
            </div>
            {brand.parent_company && (
              <div style={{ fontSize: "11px", color: "var(--text-secondary)" }}>
                by {brand.parent_company}
              </div>
            )}
            {!brand.parent_company && brand.category && (
              <div style={{ fontSize: "11px", color: "var(--text-secondary)" }}>
                {brand.category}
              </div>
            )}
          </div>
        </div>
        {onClose && <CloseBtn onClose={onClose} />}
      </div>

      {/* Score ring — fixed, never scrolls */}
      <div style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        padding: "16px",
        borderBottom: "1px solid var(--border)",
        flexShrink: 0,
      }}>
        {showRing ? (
          <ScoreRing score={8.4} size={120} />
        ) : (
          <div style={{
            height: "80px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}>
            <p style={{
              fontSize: "13px",
              fontWeight: 500,
              color: "var(--text-secondary)",
              textAlign: "center",
              lineHeight: 1.4,
            }}
              data-testid="not-enough-data-msg"
            >
              Not enough data to score
            </p>
          </div>
        )}

        {/* Confidence indicators — emoji per active dimension */}
        {dots.length > 0 && (
          <div style={{
            display: "flex",
            gap: "8px",
            margin: "8px 0 4px",
            justifyContent: "center",
            alignItems: "center",
          }}
            data-testid="confidence-dots"
          >
            {dots.map(d => (
              <span
                key={d.key}
                data-testid={`dot-${d.key}`}
                title={DOMAIN_META[d.domainName]?.label}
                style={{
                  fontSize: "18px",
                  lineHeight: 1,
                  filter: d.hasDot ? "none" : "grayscale(100%)",
                  opacity: d.hasDot ? 1 : 0.3,
                  transition: "opacity 0.2s, filter 0.2s",
                }}
              >
                {DOMAIN_META[d.domainName]?.emoji}
              </span>
            ))}
          </div>
        )}

        <p style={{ fontSize: "12px", color: "var(--text-secondary)", marginTop: "2px" }}>
          Based on your values
        </p>
      </div>

      {/* Scrollable content — domain rows + alternatives */}
      <div className="popup-scroll" style={{ flex: 1, overflowY: "auto" }}>

        {/* Domain breakdown */}
        <div data-testid="domain-breakdown">
          {DOMAIN_ORDER.map((domainName) => {
          const meta = DOMAIN_META[domainName];
          if (!meta) return null;
          const isPremium = meta.premium;
          const domRatings = groupedRatings[domainName] || [];

          return (
            <DomainRow
              key={domainName}
              domainName={domainName}
              ratings={domRatings}
              isExpanded={expandedDomain === domainName}
              onToggle={() =>
                !isPremium &&
                setExpandedDomain(expandedDomain === domainName ? null : domainName)
              }
              isPremium={isPremium}
              comingSoon={meta.comingSoon}
              weights={weights}
              brandSize={brandData?.brand?.brand_size || ""}
              brandCategory={brandData?.brand?.category || ""}
            />
          );
        })}
        </div>

        {/* Alternatives */}
        {altScores.length > 0 && (
          <div style={{ borderTop: "1px solid #F0F0F0", padding: "12px 16px" }} data-testid="alternatives-section">
            <p style={{ fontSize: "12px", fontWeight: 600, color: "var(--text-primary)", marginBottom: "8px" }}>
              Higher-scoring alternatives
            </p>
            {altScores.map((alt) => (
              <div
                key={alt.domain}
                data-testid="alternative-brand"
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "8px 10px",
                  border: "1px solid var(--border)",
                  borderRadius: "8px",
                  marginBottom: "6px",
                  cursor: "pointer",
                  transition: "border-color 0.15s",
                }}
                onMouseEnter={(e) => e.currentTarget.style.borderColor = "var(--coral)"}
                onMouseLeave={(e) => e.currentTarget.style.borderColor = "var(--border)"}
              >
                <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                  <span style={{ fontSize: "12px", fontWeight: 500 }}>{alt.name}</span>
                  <span style={{ fontSize: "11px", color: "#aaa" }}>Stronger overall ethics score</span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                  <span style={{ fontSize: "12px", fontWeight: 600, color: "var(--coral)" }}>
                    {alt.composite?.toFixed(1)} / 10
                  </span>
                  <ArrowRight size={12} color="var(--coral)" />
                </div>
              </div>
            ))}
          </div>
        )}

      </div>{/* end scrollable */}

      {/* Footer */}
      <div style={footerStyle} data-testid="popup-footer">
        <span style={{ fontSize: "11px", color: "var(--text-tertiary)" }}>
          {lastUpdated ? `Updated ${lastUpdated}` : ""}
        </span>
        {onOpenSettings && (
          <button
            onClick={onOpenSettings}
            style={footerLinkStyle}
            data-testid="settings-link"
          >
            <Settings size={12} /> My values
          </button>
        )}
      </div>
    </div>
  );
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function CoralLogo() {
  return (
    <img src="/coral-icon.webp" width="28" height="28" style={{ borderRadius: "8px", flexShrink: 0, display: "block" }} alt="Coral" />
  );
}

function CloseBtn({ onClose }) {
  return (
    <button
      onClick={onClose}
      data-testid="close-popup"
      style={{
        background: "none",
        border: "none",
        cursor: "pointer",
        padding: "4px",
        borderRadius: "6px",
        color: "var(--text-tertiary)",
        display: "flex",
        alignItems: "center",
      }}
    >
      <X size={16} />
    </button>
  );
}

const popupStyle = {
  width: "360px",
  height: "560px",
  background: "#FFFFFF",
  border: "1px solid var(--border)",
  borderRadius: "12px",
  overflow: "hidden",
  boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
  display: "flex",
  flexDirection: "column",
  fontFamily: "Inter, sans-serif",
};

const headerStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "12px 16px",
  borderBottom: "1px solid var(--border)",
  flexShrink: 0,
};

const footerStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "10px 16px",
  borderTop: "1px solid var(--border)",
  background: "#FAFAFA",
};

const footerLinkStyle = {
  display: "flex",
  alignItems: "center",
  gap: "4px",
  fontSize: "11px",
  color: "var(--text-secondary)",
  background: "none",
  border: "none",
  cursor: "pointer",
  fontFamily: "Inter, sans-serif",
  padding: 0,
};
